// Iter 31.328 (05 Aug 2026) — Manage Stations admin surface.
//
// Simple control panel per SpyRadio station:
//   - On/off toggle (writes radio_stations.active, filters public feed)
//   - Track/drop/artwork/meta counts from R2 (the CDN mirror) vs Drive
//   - One-click "Sync from Drive" per station + "Sync all"
//   - Direct link into the Cloudflare R2 dashboard folder for the station
//
// R2 is the public listener path (fast, no OAuth, no backend proxy).
// Drive stays canonical for Two's curator workflow — she keeps dropping
// files there. This page is the bridge between the two.
import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Loader2, RefreshCw, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const R2_DASHBOARD_BASE = "https://dash.cloudflare.com";

function Chip({ ok, label, value, testId }) {
    const color = value === -1
        ? "border-stamp-red text-stamp-red"
        : (ok ? "border-stamp-green text-stamp-green" : "border-ink/30 text-ink-soft");
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center gap-1 border px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${color}`}
        >
            {label} {value === -1 ? "!" : value}
        </span>
    );
}

function StationRow({ station, onToggle, onSync, syncing }) {
    const { slug, name, cover_url, active, r2_counts, drive_track_count, last_sync_at } = station;
    const r2Total = (r2_counts?.tracks || 0) + (r2_counts?.drops || 0);
    const inSync = r2Total >= drive_track_count && r2Total > 0;
    const noR2 = r2Total === 0;

    return (
        <div
            data-testid={`station-row-${slug}`}
            className={`grid grid-cols-[80px_1fr_auto] gap-4 border-b border-ink/10 py-4 ${!active ? "opacity-50" : ""}`}
        >
            {/* Cover thumb */}
            <div className="flex items-center justify-center">
                {cover_url ? (
                    <img
                        src={cover_url}
                        alt=""
                        className="h-16 w-16 border border-ink/20 object-cover"
                        data-testid={`station-cover-${slug}`}
                    />
                ) : (
                    <div className="h-16 w-16 border border-dashed border-ink/20 flex items-center justify-center text-ink-soft text-[10px] font-heading uppercase tracking-[0.15em]">
                        no art
                    </div>
                )}
            </div>

            {/* Meta */}
            <div className="min-w-0">
                <div className="flex items-baseline gap-3">
                    <h3 className="font-heading text-base tracking-[0.15em] uppercase" data-testid={`station-name-${slug}`}>
                        {name}
                    </h3>
                    <span className="font-mono text-xs text-ink-soft">{slug}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <Chip ok={inSync} label="R2·tracks" value={r2_counts?.tracks ?? 0} testId={`chip-r2-tracks-${slug}`} />
                    <Chip ok={r2_counts?.drops > 0 || drive_track_count === 0} label="R2·drops" value={r2_counts?.drops ?? 0} testId={`chip-r2-drops-${slug}`} />
                    <Chip ok={r2_counts?.artwork > 0} label="R2·art" value={r2_counts?.artwork ?? 0} testId={`chip-r2-artwork-${slug}`} />
                    <Chip ok={r2_counts?.meta > 0} label="R2·meta" value={r2_counts?.meta ?? 0} testId={`chip-r2-meta-${slug}`} />
                    <span className="border border-dashed border-ink/25 px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] text-ink-soft" data-testid={`chip-drive-${slug}`}>
                        drive·tracks {drive_track_count}
                    </span>
                </div>
                <div className="mt-2 text-xs text-ink-soft font-mono">
                    {last_sync_at ? (
                        <>last sync {new Date(last_sync_at).toLocaleString()}</>
                    ) : (
                        <span className="italic">never synced</span>
                    )}
                    {noR2 && (
                        <span className="ml-3 text-stamp-red">
                            <AlertTriangle className="inline-block h-3 w-3 mr-1 align-text-top" />
                            R2 empty — sync to populate
                        </span>
                    )}
                    {inSync && (
                        <span className="ml-3 text-stamp-green">
                            <CheckCircle2 className="inline-block h-3 w-3 mr-1 align-text-top" />
                            live on CDN
                        </span>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
                <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-2">
                        <span className="font-heading uppercase text-[10px] tracking-[0.18em] text-ink-soft">
                            {active ? "on" : "off"}
                        </span>
                        <Switch
                            checked={active}
                            onCheckedChange={(v) => onToggle(slug, v)}
                            data-testid={`toggle-active-${slug}`}
                        />
                    </div>
                </div>
                <button
                    onClick={() => onSync(slug)}
                    disabled={syncing}
                    data-testid={`btn-sync-${slug}`}
                    className="border border-ink/40 hover:border-ink px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.2em] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {syncing ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : <RefreshCw className="h-3 w-3 inline mr-1" />}
                    sync
                </button>
            </div>
        </div>
    );
}

export default function AdminStations() {
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncingSlug, setSyncingSlug] = useState(null);
    const [syncingAll, setSyncingAll] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/spyradio/status");
            setPayload(data);
        } catch (err) {
            toast.error(`Failed to load: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleToggle = async (slug, active) => {
        try {
            await api.patch(`/admin/spyradio/stations/${slug}`, { active });
            toast.success(`${slug} → ${active ? "ON" : "OFF"}`);
            await load();
        } catch (err) {
            toast.error(`Toggle failed: ${err?.response?.data?.detail || err.message}`);
        }
    };

    const handleSyncOne = async (slug) => {
        setSyncingSlug(slug);
        try {
            const { data } = await api.post(`/admin/spyradio/sync/${slug}`, { force: false, prune: false });
            const total = Object.values(data.sections || {}).reduce(
                (sum, s) => sum + ((s.uploaded || 0)),
                0,
            );
            toast.success(`${slug} synced · ${total} files uploaded`);
            await load();
        } catch (err) {
            toast.error(`Sync failed: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setSyncingSlug(null);
        }
    };

    const handleSyncAll = async () => {
        setSyncingAll(true);
        try {
            const { data } = await api.post("/admin/spyradio/sync-all", { force: false, prune: false });
            toast.success(`Synced ${data.count} stations`);
            await load();
        } catch (err) {
            toast.error(`Sync-all failed: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setSyncingAll(false);
        }
    };

    return (
        <Shell>
            <div className="space-y-6" data-testid="admin-stations-page">
                <header className="flex items-baseline justify-between border-b border-ink pb-4">
                    <div>
                        <h1 className="font-heading text-2xl tracking-[0.2em] uppercase" data-testid="admin-stations-title">
                            Manage Stations
                        </h1>
                        <p className="text-sm text-ink-soft mt-1">
                            Turn stations on/off, mirror Drive → Cloudflare R2, monitor sync health.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleSyncAll}
                            disabled={syncingAll || loading}
                            data-testid="btn-sync-all"
                            className="border border-ink/60 hover:border-ink px-4 py-2 font-heading uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                        >
                            {syncingAll ? <Loader2 className="h-3 w-3 animate-spin inline mr-2" /> : <RefreshCw className="h-3 w-3 inline mr-2" />}
                            sync all
                        </button>
                        <button
                            onClick={load}
                            disabled={loading}
                            data-testid="btn-refresh"
                            className="border border-ink/30 hover:border-ink/60 px-3 py-2 font-heading uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                        >
                            refresh
                        </button>
                    </div>
                </header>

                {/* R2 configuration status */}
                {payload?.r2 && (
                    <div
                        data-testid="r2-status-card"
                        className={`border p-4 ${payload.r2.configured ? "border-stamp-green/40 bg-stamp-green/5" : "border-stamp-red/50 bg-stamp-red/5"}`}
                    >
                        <div className="flex items-baseline justify-between">
                            <div>
                                <div className="font-heading uppercase text-[11px] tracking-[0.22em]">
                                    Cloudflare R2 · {payload.r2.configured ? "wired" : "not configured"}
                                </div>
                                {payload.r2.configured && (
                                    <div className="mt-1 text-xs font-mono text-ink-soft">
                                        bucket <b>{payload.r2.bucket}</b> · public {payload.r2.public_base}
                                    </div>
                                )}
                                {!payload.r2.configured && (
                                    <div className="mt-1 text-xs text-stamp-red">
                                        Missing env vars: {["R2_ENDPOINT","R2_ACCESS_KEY_ID","R2_SECRET_ACCESS_KEY","R2_BUCKET","R2_PUBLIC_BASE"]
                                            .filter(k => !payload.r2[k.toLowerCase().replace(/^r2_/, "") + "_present"])
                                            .join(", ") || "(check backend logs)"}
                                    </div>
                                )}
                            </div>
                            {payload.r2.configured && (
                                <a
                                    href={`${R2_DASHBOARD_BASE}/`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="link-r2-dashboard"
                                    className="text-xs font-heading uppercase tracking-[0.18em] text-ink-soft hover:text-ink inline-flex items-center gap-1"
                                >
                                    R2 dashboard <ExternalLink className="h-3 w-3" />
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {loading && !payload && (
                    <div className="flex items-center gap-2 text-ink-soft" data-testid="loading-state">
                        <Loader2 className="h-4 w-4 animate-spin" /> loading…
                    </div>
                )}

                {!loading && payload?.stations?.length === 0 && (
                    <div className="text-ink-soft italic text-sm" data-testid="empty-state">
                        No stations found. Two hasn&apos;t created any radio folders in Drive yet, or Drive is unreachable.
                    </div>
                )}

                <div data-testid="stations-list">
                    {payload?.stations?.map(s => (
                        <StationRow
                            key={s.slug}
                            station={s}
                            onToggle={handleToggle}
                            onSync={handleSyncOne}
                            syncing={syncingSlug === s.slug}
                        />
                    ))}
                </div>

                <div className="text-xs text-ink-soft italic pt-4">
                    Chips show R2 file counts per section (tracks / drops / artwork / meta).
                    Green = live on CDN, grey = empty, red = R2 call failed.
                    The <b>drive·tracks</b> chip is Two&apos;s canonical count from Google Drive — the sync target.
                </div>
            </div>
        </Shell>
    );
}
